import { getSupportedDevicesSync } from "./supportedDevicesStore";
import { hasDeviceExternalUrl } from "./deviceConnectionUtils";
import { isGenericHidKeyboardProductName } from "./genericKeyboardOtherOnly";

const normProductName = (s: unknown) =>
  typeof s === "string" ? s.trim().toLowerCase() : "";

let vidPidNeedProductSplitCache: Set<string> | null = null;

function getKeyboardVidPidNeedProductSplitSet(): Set<string> {
  if (vidPidNeedProductSplitCache) {
    return vidPidNeedProductSplitCache;
  }
  const kb = getSupportedDevicesSync().keyboard as any[];
  const byVidPid = new Map<string, Set<string>>();
  for (const item of kb) {
    if (
      typeof item.vendorId !== "number" ||
      typeof item.productId !== "number"
    ) {
      continue;
    }
    const key = `${item.vendorId}-${item.productId}`;
    const pn = normProductName(item.productName);
    if (!pn) continue;
    if (!byVidPid.has(key)) byVidPid.set(key, new Set());
    byVidPid.get(key)!.add(pn);
  }
  const need = new Set<string>();
  for (const [key, names] of byVidPid) {
    if (names.size > 1) need.add(key);
  }
  vidPidNeedProductSplitCache = need;
  return need;
}

/**
 * 配置表中同 VID/PID 对应多个 productName 时需按名称拆组/匹配（如 ES68 Dawn / ES68 EVO）。
 * 延迟构建，避免在 ensureSupportedDevices 之前于模块顶层访问配置表。
 */
export function keyboardVidPidSplitKeyNeedsNameSplit(
  vidPidKey: string
): boolean {
  return getKeyboardVidPidNeedProductSplitSet().has(vidPidKey);
}

/** @deprecated 请用 keyboardVidPidSplitKeyNeedsNameSplit，保留兼容导出 */
export const KEYBOARD_VID_PID_NEED_PRODUCT_SPLIT: Set<string> = {
  has(key: string) {
    return keyboardVidPidSplitKeyNeedsNameSplit(key);
  },
} as Set<string>;

export function keyboardVidPidNeedsProductNameSplit(
  vendorId: number,
  productId: number
): boolean {
  return keyboardVidPidSplitKeyNeedsNameSplit(`${vendorId}-${productId}`);
}

/** 键盘星闪固件升级进行中（子应用写入 localStorage，主应用热插拔逻辑需识别） */
export function isKeyboardFirmwareUpgradeInProgress(): boolean {
  try {
    return (
      typeof localStorage !== "undefined" &&
      localStorage.getItem("upgrade_firmware_data") === "true"
    );
  } catch {
    return false;
  }
}

function configurationDeviceIdentifier(configuration: any): string {
  return String(
    configuration?.identifier ??
      configuration?.uuid ??
      configuration?.deviceId ??
      ""
  ).trim();
}

// function xingshanSplitVidPidFallbackMatch(
//   configuration: any,
//   device: any,
//   cfgPn: string,
//   devPn: string
// ): boolean {
//   if (cfgPn && devPn && cfgPn === devPn) return true;
//   const cfgIdent = configurationDeviceIdentifier(configuration);
//   if (cfgIdent && device?.id != null && cfgIdent === String(device.id)) {
//     return true;
//   }
//   const cfgKt = String(configuration?.keyboardType ?? "").trim();
//   const devKt = String(device?.keyboardType ?? "").trim();
//   if (cfgKt && devKt && cfgKt === devKt) return true;
//   return false;
// }

/**
 * 星闪 HID/SDK 设备是否与配置表行对应（同 VID/PID 多型号须 productName 一致）。
 * 实机 productName 为通用 "keyboard" 时仅按 VID/PID 匹配（用于建产品卡片）。
 */
/**
 * 容园机械轴驱动内方案（solutionRovider: "rongyuan"，当前：X87S）。
 * 容园磁轴外链跳转用 "rongyuan2"（如 DEEP Pro/DEEP68），勿走 rongyuan 建链。
 */
export function isRongyuanMechanicalKeyboard(configuration: any): boolean {
  if (!configuration || configuration.solutionRovider !== "rongyuan") {
    return false;
  }
  if (configuration.magneticAxis === true) return false;
  if (hasDeviceExternalUrl(configuration)) return false;
  return true;
}

/** 容园 HID Feature 接口是否与配置表 usagePage/usage 一致（默认 0xffb0 / 0x01） */
export function rongyuanHidCollectionMatches(
  configuration: any,
  collection: any
): boolean {
  if (!isRongyuanMechanicalKeyboard(configuration)) {
    return false;
  }
  const usagePage =
    typeof configuration.usagePage === "number"
      ? configuration.usagePage
      : 0xffb0;
  const usage =
    typeof configuration.usage === "number" ? configuration.usage : 0x01;
  return collection?.usagePage === usagePage && collection?.usage === usage;
}

/** 容园 HID 是否与配置表行对应（同 VID/PID 多型号须 productName 一致） */
export function rongyuanHidMatchesConfiguration(
  configuration: any,
  device: any
): boolean {
  if (!isRongyuanMechanicalKeyboard(configuration)) {
    return false;
  }
  if (
    device?.vendorId !== configuration.vendorId ||
    device?.productId !== configuration.productId
  ) {
    return false;
  }
  if (isGenericHidKeyboardProductName(device?.productName)) {
    return true;
  }
  const vidPidKey = `${configuration.vendorId}-${configuration.productId}`;
  const cfgPn = normProductName(configuration.productName);
  const devPn = normProductName(device?.productName);
  if (keyboardVidPidSplitKeyNeedsNameSplit(vidPidKey)) {
    return Boolean(cfgPn) && Boolean(devPn) && cfgPn === devPn;
  }
  return !cfgPn || !devPn || cfgPn === devPn;
}

export function xingshanHidMatchesConfiguration(
  configuration: any,
  device: any
): boolean {
  if (!configuration || configuration.solutionRovider !== "xingshan") {
    return false;
  }
  if (
    device?.vendorId !== configuration.vendorId ||
    device?.productId !== configuration.productId
  ) {
    return false;
  }
  /** HID 上报通用名 "keyboard"：无法按型号名区分，退化为 VID/PID */
  if (isGenericHidKeyboardProductName(device?.productName)) {
    return true;
  }
  const vidPidKey = `${configuration.vendorId}-${configuration.productId}`;
  const cfgPn = normProductName(configuration.productName);
  const devPn = normProductName(device?.productName);
  if (keyboardVidPidSplitKeyNeedsNameSplit(vidPidKey)) {
    return Boolean(cfgPn) && Boolean(devPn) && cfgPn === devPn;
  }
  return !cfgPn || !devPn || cfgPn === devPn;
}

export function readKeyboardEquipmentDataFromStorage(): any | null {
  try {
    if (typeof window === "undefined") return null;
    const raw =
      window.sessionStorage.getItem("eweadn:session:equipmentData") ??
      (typeof localStorage !== "undefined"
        ? localStorage.getItem("equipmentData")
        : null);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** 固件升级前缓存的星闪 SDK 设备 id（Boot 重连时 productName 可能为空） */
export const UPGRADE_XINGSHAN_DEVICE_ID_KEY = "upgrade_xingshan_device_id";

export function readUpgradeXingshanDeviceIdFromStorage(): string {
  try {
    return String(
      localStorage.getItem(UPGRADE_XINGSHAN_DEVICE_ID_KEY) ?? ""
    ).trim();
  } catch {
    return "";
  }
}

export function writeUpgradeXingshanDeviceIdToStorage(deviceId: unknown): void {
  if (deviceId == null || String(deviceId).trim() === "") return;
  try {
    localStorage.setItem(UPGRADE_XINGSHAN_DEVICE_ID_KEY, String(deviceId));
  } catch {
    /* ignore */
  }
}

export function clearUpgradeXingshanDeviceIdFromStorage(): void {
  try {
    localStorage.removeItem(UPGRADE_XINGSHAN_DEVICE_ID_KEY);
  } catch {
    /* ignore */
  }
}

/** 固件升级断连重连：按 equipmentData / SDK id 解析目标设备，避免同 VID/PID 误 init [0] */
export function resolveXingshanSdkDeviceForFirmwareReconnect(
  equipmentData: any,
  deviceList: any[]
): any | null {
  if (!Array.isArray(deviceList) || deviceList.length === 0) return null;
  const cachedId = readUpgradeXingshanDeviceIdFromStorage();
  if (cachedId) {
    const byCached = deviceList.find(
      (d) => d?.id != null && String(d.id) === cachedId
    );
    if (byCached) return byCached;
  }
  const eq = equipmentData;
  if (eq) {
    const ident = configurationDeviceIdentifier(eq);
    if (ident) {
      const byId = deviceList.find(
        (d) => d?.id != null && String(d.id) === ident
      );
      if (byId) return byId;
    }
    const matched = deviceList.filter((d) =>
      xingshanHidMatchesConfiguration(eq, d)
    );
    if (matched.length === 1) return matched[0];
    if (matched.length > 1 && ident) {
      return matched.find((d) => String(d.id) === ident) ?? null;
    }
    const sameVidPid = deviceList.filter(
      (d) => d?.vendorId === eq.vendorId && d?.productId === eq.productId
    );
    if (sameVidPid.length === 1) return sameVidPid[0];
    const cfgKt = String(eq.keyboardType ?? "").trim();
    if (cfgKt) {
      const byKt = sameVidPid.filter(
        (d) => String(d?.keyboardType ?? "").trim() === cfgKt
      );
      if (byKt.length === 1) return byKt[0];
    }
  }
  return deviceList.length === 1 ? deviceList[0] : null;
}

/**
 * 星闪 SDK usbChange 断开是否对应当前操作会话（子应用 / 内页应回首页）。
 * 勿仅比对 connectList 与 configurationNow 的 VID/PID（子应用卡片 equipment 可能已对齐、列表为空）。
 */
export function isXingshanDisconnectMatchingActiveSession(
  disconnectDetail: unknown,
  options?: {
    configurationNow?: Record<string, unknown> | null;
    equipmentData?: Record<string, unknown> | null;
    connectList?: Array<{
      configuration?: Record<string, unknown>;
      device?: { id?: unknown };
    }>;
  }
): boolean {
  const data = disconnectDetail as { device?: Record<string, unknown> };
  const dev = data?.device;
  if (!dev) return false;

  const sdkId = dev.id;
  const activeCandidates: Record<string, unknown>[] = [];

  const now = options?.configurationNow;
  if (now && String(now.solutionRovider ?? "") === "xingshan") {
    activeCandidates.push(now);
  }

  const eq = options?.equipmentData ?? readKeyboardEquipmentDataFromStorage();
  if (
    eq &&
    String(eq.solutionRovider ?? "") === "xingshan" &&
    !activeCandidates.includes(eq)
  ) {
    activeCandidates.push(eq);
  }

  if (activeCandidates.length === 0) return false;

  for (const active of activeCandidates) {
    const activeIdent = String(
      active.identifier ?? active.uuid ?? active.deviceId ?? ""
    ).trim();
    if (sdkId != null && activeIdent && String(sdkId) === activeIdent) {
      return true;
    }
    if (xingshanHidMatchesConfiguration(active, dev)) {
      return true;
    }
  }

  const list = options?.connectList ?? [];
  for (const item of list) {
    if (item?.configuration?.solutionRovider !== "xingshan") continue;
    if (sdkId != null && item?.device?.id != null && item.device.id !== sdkId) {
      continue;
    }
    for (const active of activeCandidates) {
      const cfg = item.configuration;
      if (
        cfg &&
        Number(cfg.productId) === Number(active.productId) &&
        Number(cfg.vendorId) === Number(active.vendorId)
      ) {
        return true;
      }
    }
  }

  return false;
}

/** 按实机 VID/PID + productName 解析星闪配置表行（避免沿用错误 VID 的历史卡片） */
export function resolveXingshanConfigurationRowForDevice(
  device: any
): any | null {
  if (!device) return null;
  const kb = getSupportedDevicesSync().keyboard as any[];
  for (const row of kb) {
    if (
      row?.solutionRovider === "xingshan" &&
      row.isSupported !== false &&
      xingshanHidMatchesConfiguration(row, device)
    ) {
      return row;
    }
  }
  return null;
}

/** ES68 Dawn / ES68 Lite / ES68 EVO：四槽板载预设名 + es68Card 卡片 */
export function supportsEs68OnboardPresetCards(
  config?: {
    solutionRovider?: unknown;
    keyboardType?: unknown;
    displayname?: unknown;
    productId?: unknown;
    vendorId?: unknown;
  } | null
): boolean {
  if (!config || String(config.solutionRovider ?? "") !== "xingshan") {
    return false;
  }

  const displayname = String(config.displayname ?? "").trim();
  const keyboardType = String(config.keyboardType ?? "");
  if (keyboardType === "68EVO" && displayname === "ES68 EVO") return true;
  if (
    keyboardType === "68" &&
    (displayname === "ES68" ||
      displayname === "ES68 Lite" ||
      displayname === "ES68 TIANYI" ||
      displayname === "ES68 EVO (Full)")
  ) {
    return true;
  }

  const productId = Number(config.productId);
  const vendorId = Number(config.vendorId);
  return (
    (productId === 7204 && vendorId === 7334) ||
    (productId === 65153 && vendorId === 6790) ||
    (productId === 7215 && vendorId === 7334) ||
    (productId === 7242 && vendorId === 7334) ||
    (productId === 7244 && vendorId === 7334) ||
    (productId === 7248 && vendorId === 7334)
  );
}

/** ES68 Dawn / EVO / TIANYI（洛天依）等：USB3 下可选 16K / 32K 回报率 */
export function supportsEs68Usb3Polling(
  config?: {
    solutionRovider?: unknown;
    displayname?: unknown;
    productName?: unknown;
    productId?: unknown;
    vendorId?: unknown;
  } | null
): boolean {
  if (!config || String(config.solutionRovider ?? "") !== "xingshan") {
    return false;
  }

  const displayname = String(config.displayname ?? "").trim();
  if (
    displayname === "ES68" ||
    displayname === "ES68 EVO" ||
    displayname === "ES68 EVO (Full)" ||
    displayname === "ES68 TIANYI"
  ) {
    return true;
  }

  const productName = String(config.productName ?? "");
  if (/\bEWEADN\s*ES68\s*DAWN\b/i.test(productName)) return true;
  if (/\bEWEADN\s*ES68\s*EVO\b/i.test(productName)) return true;
  if (/\bES68\s*TIANYI\b/i.test(productName)) return true;

  const productId = Number(config.productId);
  const vendorId = Number(config.vendorId);
  return (
    (productId === 0x1c24 && vendorId === 0x1ca6) ||
    (productId === 0x1c2f && vendorId === 0x1ca6) ||
    (productId === 0x1c4c && vendorId === 0x1ca6) ||
    (productId === 0x1c50 && vendorId === 0x1ca6) ||
    (productId === 65153 &&
      vendorId === 6790 &&
      (/\bES68\s*DAWN\b/i.test(productName) ||
        /\bES68\s*EVO\b/i.test(productName)))
  );
}

/** ES68 Dawn / ES68 Lite（非 EVO）：Win 键锁定（协议 0x0a 0x03） */
export function supportsEs68WinLock(
  config?: {
    solutionRovider?: unknown;
    keyboardType?: unknown;
    displayname?: unknown;
    productName?: unknown;
    productId?: unknown;
    vendorId?: unknown;
  } | null
): boolean {
  if (!config || String(config.solutionRovider ?? "") !== "xingshan") {
    return false;
  }

  const displayname = String(config.displayname ?? "").trim();
  const keyboardType = String(config.keyboardType ?? "");
  const productName = String(config.productName ?? "");
  if (keyboardType === "68EVO" || displayname === "ES68 EVO") return false;
  if (
    keyboardType === "68" &&
    (displayname === "ES68" ||
      displayname === "ES68 Lite" ||
      displayname === "ES68 TIANYI" ||
      displayname === "ES68 EVO (Full)")
  ) {
    return true;
  }
  if (/\bEWEADN\s*ES68\s*DAWN\b/i.test(productName)) return true;
  if (/\bES68\s*TIANYI\b/i.test(productName)) return true;
  if (
    /\bEWEADN\s*ES68\s*EVO\b/i.test(productName) &&
    displayname === "ES68 EVO (Full)"
  ) {
    return true;
  }

  const productId = Number(config.productId);
  const vendorId = Number(config.vendorId);
  return (
    (productId === 7204 && vendorId === 7334) ||
    (productId === 7248 && vendorId === 7334) ||
    (productId === 65153 &&
      vendorId === 6790 &&
      /\bES68\s*DAWN\b/i.test(productName)) ||
    (productId === 7242 && vendorId === 7334) ||
    (productId === 7244 && vendorId === 7334)
  );
}
